import { addDays, addMonths, format, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import { forwardRef, useMemo, useRef, useState, type InputHTMLAttributes } from 'react';
import { dateFormat } from '@/lib/format';
import { useLocale, useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useNativeControl } from './native';
import { Popover, PopoverPanel, PopoverTrigger } from './popover';
import { controlClass } from './styles';

export type DateTimeKind = 'date' | 'datetime-local' | 'time';

const DAY = 'yyyy-MM-dd';

function parseDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function splitValue(kind: DateTimeKind, value: string): { day: string; time: string } {
  if (kind === 'time') return { day: '', time: value.slice(0, 5) };
  if (kind === 'date') return { day: value.slice(0, 10), time: '' };
  const [day = '', time = ''] = value.split('T');
  return { day, time: time.slice(0, 5) };
}

function joinValue(kind: DateTimeKind, day: string, time: string): string {
  if (kind === 'time') return time;
  if (kind === 'date') return day;
  if (!day) return '';
  return `${day}T${time || '00:00'}`;
}

function displayValue(kind: DateTimeKind, value: string, locale: string): string {
  const { day, time } = splitValue(kind, value);
  if (kind === 'time') return time;
  const date = parseDay(day);
  if (!date) return '';
  const shown = dateFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  return kind === 'datetime-local' && time ? `${shown}, ${time}` : shown;
}

function Calendar({ selected, min, max, locale, onPick }: { selected: string; min?: string; max?: string; locale: string; onPick: (day: string) => void }) {
  const t = useT();
  const today = new Date();
  const selectedDate = parseDay(selected);
  const [month, setMonth] = useState(() => startOfMonth(selectedDate ?? today));
  const [shownFor, setShownFor] = useState(selected);

  if (selected !== shownFor) {
    setShownFor(selected);
    if (selectedDate) setMonth(startOfMonth(selectedDate));
  }

  const weekdays = useMemo(() => {
    const formatter = dateFormat(locale, { weekday: 'narrow' });
    const first = startOfWeek(new Date(2024, 0, 1), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => formatter.format(addDays(first, i)));
  }, [locale]);

  const days = useMemo(() => {
    const first = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [month]);

  const monthLabel = dateFormat(locale, { month: 'long', year: 'numeric' }).format(month);

  return (
    <div className="w-[17.5rem] p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <button type="button" onClick={() => setMonth(addMonths(month, -1))} aria-label={t('control.previousMonth')} className="cursor-pointer rounded-[8px] p-1.5 text-ink-muted transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <span aria-live="polite" className="text-sm font-bold capitalize tracking-[-0.01em] text-ink">
          {monthLabel}
        </span>
        <button type="button" onClick={() => setMonth(addMonths(month, 1))} aria-label={t('control.nextMonth')} className="cursor-pointer rounded-[8px] p-1.5 text-ink-muted transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5" role="grid">
        {weekdays.map((day, i) => (
          <span key={i} className="pb-1 text-center text-[11px] font-semibold uppercase text-slate-400" aria-hidden>
            {day}
          </span>
        ))}
        {days.map((day) => {
          const key = format(day, DAY);
          const outside = !isSameMonth(day, month);
          const blocked = (min !== undefined && key < min.slice(0, 10)) || (max !== undefined && key > max.slice(0, 10));
          const isSelected = selectedDate !== null && isSameDay(day, selectedDate);
          return (
            <button
              key={key}
              type="button"
              disabled={blocked}
              aria-pressed={isSelected}
              onClick={() => onPick(key)}
              className={cn(
                'h-9 rounded-[8px] text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300',
                blocked ? 'cursor-not-allowed text-slate-300' : 'cursor-pointer hover:bg-slate-100',
                outside && !isSelected && 'text-slate-400',
                !outside && !isSelected && 'text-ink',
                isSelected && 'bg-ink text-white hover:bg-slate-800',
                !isSelected && !blocked && isSameDay(day, today) && 'ring-1 ring-inset ring-slate-300',
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TimeColumns({ value, onPick }: { value: string; onPick: (time: string) => void }) {
  const t = useT();
  const [hour = '', minute = ''] = value ? value.split(':') : [];
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
    return minute && !base.includes(minute) ? [...base, minute].sort() : base;
  }, [minute]);

  return (
    <div className="flex w-[9.5rem] gap-1 border-line p-2">
      {[
        { key: 'hour', label: t('control.hour'), items: hours, current: hour, pick: (h: string) => onPick(`${h}:${minute || '00'}`) },
        { key: 'minute', label: t('control.minute'), items: minutes, current: minute, pick: (m: string) => onPick(`${hour || '00'}:${m}`) },
      ].map((column) => (
        <div key={column.key} className="min-w-0 flex-1">
          <p className="pb-1 text-center text-[11px] font-semibold uppercase text-slate-400">{column.label}</p>
          <div className="scrollbar-thin flex max-h-52 flex-col gap-0.5 overflow-y-auto" role="listbox" aria-label={column.label}>
            {column.items.map((item) => {
              const active = item === column.current;
              return (
                <button
                  key={item}
                  type="button"
                  role="option"
                  aria-selected={active}
                  ref={active ? (node) => node?.scrollIntoView({ block: 'center' }) : undefined}
                  onClick={() => column.pick(item)}
                  className={cn('cursor-pointer rounded-[8px] py-1.5 text-center text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300', active ? 'bg-ink text-white' : 'text-ink hover:bg-slate-100')}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface DateTimeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type: DateTimeKind;
  invalid?: boolean;
}

/**
 * A date, time or date-and-time field with Bulk's own calendar instead of the
 * browser's. As with `Select`, the real input keeps the value in exactly the
 * format the native control would produce, so callers see no difference.
 */
export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(function DateTimeInput({ type, className, invalid, disabled, id, title, placeholder, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, ...props }, ref) {
  const locale = useLocale();
  const t = useT();
  const { ref: nativeRef, value, commit } = useNativeControl<HTMLInputElement>(ref);
  const [open, setOpen] = useState(false);
  const closeAfterTime = useRef(false);
  const { day, time } = splitValue(type, value);
  const min = typeof props.min === 'string' ? props.min : undefined;
  const max = typeof props.max === 'string' ? props.max : undefined;
  const shown = displayValue(type, value, locale);
  const Icon = type === 'time' ? Clock : CalendarIcon;

  function pickDay(next: string) {
    commit(joinValue(type, next, time));
    if (type === 'date') setOpen(false);
    else closeAfterTime.current = true;
  }

  function pickTime(next: string) {
    commit(joinValue(type, day || format(new Date(), DAY), next));
    if (type === 'time' || closeAfterTime.current) {
      closeAfterTime.current = false;
      setOpen(false);
    }
  }

  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <input ref={nativeRef} type={type} disabled={disabled} tabIndex={-1} aria-hidden className="sr-only" {...props} />
      <Popover
        open={open}
        onOpenChange={(next) => {
          closeAfterTime.current = false;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            title={title}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-invalid={invalid || undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            className={cn(controlClass({ invalid }), 'flex cursor-pointer items-center gap-2 pl-3 pr-2.5 text-left', open && 'border-slate-400 ring-2 ring-slate-200')}
          >
            <Icon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            <span className={cn('min-w-0 flex-1 truncate', shown ? 'text-ink' : 'text-slate-400')}>{shown || placeholder || t(type === 'time' ? 'control.pickTime' : 'control.pickDate')}</span>
          </button>
        </PopoverTrigger>
        {value && !props.required && !disabled ? (
          <button type="button" onClick={() => commit('')} aria-label={t('control.clear')} className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-[6px] p-1 text-slate-400 transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
        <PopoverPanel className="flex divide-x divide-line">
          {type !== 'time' ? <Calendar selected={day} min={min} max={max} locale={locale} onPick={pickDay} /> : null}
          {type !== 'date' ? <TimeColumns value={time} onPick={pickTime} /> : null}
        </PopoverPanel>
      </Popover>
    </div>
  );
});
